'use client';

import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { CheckSquare as CheckSquareIcon } from '@phosphor-icons/react/dist/ssr/CheckSquare';
import { EnvelopeSimple as EnvelopeSimpleIcon } from '@phosphor-icons/react/dist/ssr/EnvelopeSimple';
import { HouseSimple as HouseSimpleIcon } from '@phosphor-icons/react/dist/ssr/HouseSimple';
import { PaperPlaneTilt as PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/ssr/PaperPlaneTilt';
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X';

import { useNavigate } from 'react-router-dom';

import { dayjs } from '@/lib/v2/dayjs';
import { useNotifications } from '@/hooks/v2/use-notifications';
import type { AppNotification } from '@/contexts/notifications/notification-provider';
import { getNotificationLink } from '@/contexts/notifications/notification-link';

export interface NotificationsPopoverProps {
  anchorEl: null | Element;
  onClose?: () => void;
  open?: boolean;
}

export function NotificationsPopover({ anchorEl, onClose, open = false }: NotificationsPopoverProps): React.JSX.Element {
  const { notifications, unreadCount, isLoading, markAllAsRead, dismiss } = useNotifications();

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      onClose={onClose}
      open={open}
      slotProps={{
        paper: {
          sx: {
            width: '400px',
            border: '1px solid var(--mui-palette-divider)',
            boxShadow: 'var(--mui-shadows-16)',
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="h6">Notifications</Typography>
          {unreadCount > 0 && (
            <Chip color="error" label={unreadCount} size="small" />
          )}
        </Stack>
        <Tooltip title="Mark all as read">
          <span>
            <IconButton
              disabled={unreadCount === 0 || isLoading}
              edge="end"
              onClick={() => { markAllAsRead().catch(() => {}); }}
            >
              <EnvelopeSimpleIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Divider />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : notifications.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <CheckSquareIcon size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
          <Typography color="text.secondary" variant="body2">
            You're all caught up!
          </Typography>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          <List disablePadding>
            {notifications.map((notification, index) => (
              <NotificationItem
                divider={index < notifications.length - 1}
                key={notification.id}
                notification={notification}
                onClose={onClose}
                onDismiss={() => { dismiss(notification.id).catch(() => {}); }}
              />
            ))}
          </List>
        </Box>
      )}
    </Popover>
  );
}

interface NotificationItemProps {
  divider?: boolean;
  notification: AppNotification;
  onClose?: () => void;
  onDismiss?: () => void;
}

/** Derive a human-readable title from the stored Laravel class name when `data.title` is missing. */
function classToTitle(type: string): string {
  // e.g. "App\\Notifications\\Request\\RequestCreatedNotification" → "Request Created"
  const parts = type.split('\\');
  const className = parts[parts.length - 1] ?? type;
  return className
    .replace(/Notification$/, '')
    .replace(/([A-Z])/g, ' $1')
    .trim();
}

function NotificationItem({ divider, notification, onClose, onDismiss }: NotificationItemProps): React.JSX.Element {
  const { markAsRead } = useNotifications();
  const navigate = useNavigate();
  const isUnread = !notification.read_at;
  const data = notification.data;

  const title = data.title || classToTitle(notification.type);
  const body = data.body as string | undefined;
  const link = getNotificationLink(notification);
  const isClickable = isUnread || Boolean(link);

  const handleClick = () => {
    if (isUnread) {
      markAsRead(notification.id).catch(() => {});
    }
    if (link) {
      onClose?.();
      navigate(link);
    }
  };

  return (
    <ListItem
      alignItems="flex-start"
      divider={divider}
      onClick={handleClick}
      sx={{
        cursor: isClickable ? 'pointer' : 'default',
        bgcolor: isUnread ? 'action.hover' : 'transparent',
        borderLeft: isUnread ? '3px solid' : '3px solid transparent',
        borderColor: isUnread ? 'primary.main' : 'transparent',
        '&:hover': { bgcolor: 'action.selected' },
        pr: 1,
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
        <NotificationIcon type={data.type as string | undefined} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: isUnread ? 700 : 400, lineHeight: 1.3 }} variant="body2">
            {title}
          </Typography>
          {body && (
            <Typography
              color="text.secondary"
              variant="body2"
              sx={{ mt: 0.25, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
            >
              {body}
            </Typography>
          )}
          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="caption">
            {dayjs(notification.created_at).fromNow()}
          </Typography>
        </Box>
        <Tooltip title="Dismiss">
          <IconButton
            edge="end"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss?.();
            }}
            size="small"
          >
            <XIcon size={14} />
          </IconButton>
        </Tooltip>
      </Stack>
    </ListItem>
  );
}

function NotificationIcon({ type }: { type?: string }): React.JSX.Element {
  if (type === 'new_request') {
    return (
      <Avatar sx={{ bgcolor: 'primary.light', width: 36, height: 36 }}>
        <PaperPlaneTiltIcon size={18} />
      </Avatar>
    );
  }
  if (type === 'arc') {
    return (
      <Avatar sx={{ bgcolor: 'secondary.light', width: 36, height: 36 }}>
        <HouseSimpleIcon size={18} />
      </Avatar>
    );
  }
  return (
    <Avatar sx={{ width: 36, height: 36 }}>
      <EnvelopeSimpleIcon size={18} />
    </Avatar>
  );
}
